/**
 * Empire-PWA chassis · camera.
 *
 * - openStream / closeStream: getUserMedia rear/front
 * - snapshotPng: capture current frame as PNG Blob
 * - ocrViaTesseract: dynamic-import @ runtime, ~1 MB; only when user clicks Camera-OCR
 * - hasLidar: WebXR depth-sensing probe (iPhone Pro Lidar via Safari 17.4+ flag)
 *
 * All pixels are processed locally. No frame leaves the device unless caller
 * explicitly POSTs (we never do).
 */

export type FacingMode = 'user' | 'environment';

export interface OpenedStream {
  readonly stream: MediaStream;
  readonly video: HTMLVideoElement;
}

export async function openStream(facing: FacingMode = 'environment'): Promise<OpenedStream> {
  const stream = await navigator.mediaDevices.getUserMedia({
    video: { facingMode: facing, width: { ideal: 1920 }, height: { ideal: 1080 } },
    audio: false,
  });
  const video = document.createElement('video');
  video.autoplay = true;
  video.playsInline = true;
  video.muted = true;
  video.srcObject = stream;
  await video.play();
  return { stream, video };
}

export function closeStream(o: OpenedStream): void {
  o.stream.getTracks().forEach((t) => t.stop());
  o.video.srcObject = null;
}

export async function snapshotBlob(o: OpenedStream, mime: string = 'image/png', quality: number = 0.95): Promise<Blob> {
  const c = document.createElement('canvas');
  c.width = o.video.videoWidth;
  c.height = o.video.videoHeight;
  const ctx = c.getContext('2d');
  if (!ctx) throw new Error('canvas 2D context missing');
  ctx.drawImage(o.video, 0, 0);
  return new Promise((resolve, reject) => {
    c.toBlob((b) => b ? resolve(b) : reject(new Error('snapshot failed')), mime, quality);
  });
}

export async function ocrViaTesseract(blob: Blob, lang: string = 'eng'): Promise<string> {
  try {
    // @ts-expect-error -- CDN-loaded module; types not bundled in v0
    const Tesseract = (await import('https://cdn.jsdelivr.net/npm/tesseract.js@5.1.1/+esm')) as { recognize(img: Blob, lang: string): Promise<{ data: { text: string } }> };
    const { data } = await Tesseract.recognize(blob, lang);
    return data.text.trim();
  } catch {
    return '';
  }
}

export async function hasLidar(): Promise<boolean> {
  const xr = (navigator as Navigator & { xr?: { isSessionSupported(mode: string): Promise<boolean> } }).xr;
  if (!xr) return false;
  try {
    return await xr.isSessionSupported('immersive-ar');
  } catch {
    return false;
  }
}
